import { useEffect, useMemo, useRef, useState } from "react";
import type { ConversationMessage } from "../lib/api";
import { getConfig } from "../lib/config";

export type WsStatus = "connecting" | "connected" | "disconnected";

export interface ConversationSocketOptions {
  dealerId: string | null | undefined;
  enabled?: boolean;
  onMessage?: (message: ConversationMessage) => void;
}

export interface ConversationSocketResult {
  status: WsStatus;
}

const RECONNECT_MAX_MS = 30_000;

/**
 * Opens a WebSocket to the dashboard broadcast API for a single dealer's
 * conversation, invokes `onMessage` for every inbound "conversation_message"
 * frame (both inbound-from-dealer and outbound-from-bot echoes), and returns
 * a status the caller can render as a connection badge. Handles reconnect
 * with exponential backoff up to 30s, filters out frames for other dealers,
 * and tears down cleanly on unmount / dealerId change.
 *
 * Callers own message-state deduplication (by messageId) since they typically
 * maintain their own message array with optimistic pending items and
 * historical pages; this hook deliberately does not manage that state.
 */
export function useConversationSocket(options: ConversationSocketOptions): ConversationSocketResult {
  const { dealerId, enabled = true, onMessage } = options;
  const [status, setStatus] = useState<WsStatus>("connecting");
  const wsBaseUrl = useMemo(() => getConfig().wsBaseUrl.replace(/\/$/, ""), []);
  const onMessageRef = useRef(onMessage);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!enabled || !dealerId || !wsBaseUrl) {
      setStatus("disconnected");
      return;
    }

    let disposed = false;
    let reconnectAttempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let socket: WebSocket | null = null;

    const connect = () => {
      if (disposed) return;
      setStatus("connecting");
      const url = `${wsBaseUrl}?dealerId=${encodeURIComponent(dealerId)}`;
      console.log("[ws] connecting", url);
      socket = new WebSocket(url);

      socket.onopen = () => {
        if (disposed) { socket?.close(); return; }
        console.log("[ws] connected");
        setStatus("connected");
        reconnectAttempt = 0;
      };

      socket.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data) as {
            type?: string;
            dealerId?: string;
            message?: ConversationMessage;
          };
          if (parsed.type !== "conversation_message" || !parsed.message) return;
          if (parsed.dealerId && parsed.dealerId !== dealerId) return;
          onMessageRef.current?.(parsed.message);
        } catch {
          return;
        }
      };

      socket.onerror = (err) => {
        console.error("[ws] error", err);
      };

      socket.onclose = (event) => {
        if (disposed) return;
        console.log("[ws] closed", { code: event.code, reason: event.reason, wasClean: event.wasClean });
        setStatus("disconnected");
        socket = null;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempt), RECONNECT_MAX_MS);
        console.log(`[ws] reconnecting in ${delay}ms (attempt ${reconnectAttempt + 1})`);
        reconnectAttempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      disposed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (socket) {
        socket.close();
        socket = null;
      }
    };
  }, [dealerId, enabled, wsBaseUrl]);

  return { status };
}
